import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'

export interface FloatingAction {
  id: string
  content: ReactNode
  priority?: number // Higher priority actions take precedence
  persistent?: boolean // Whether to keep showing even when other actions register
}

interface FloatingActionIslandContextType {
  registerActions: (actions: FloatingAction[], componentId: string) => void
  clearActions: (componentId: string) => void
  currentActions: FloatingAction[]
  isVisible: boolean
}

const FloatingActionIslandContext =
  createContext<FloatingActionIslandContextType | null>(null)

interface FloatingActionIslandProviderProps {
  children: ReactNode
}

export function FloatingActionIslandProvider({
  children,
}: FloatingActionIslandProviderProps) {
  const [actionMap, setActionMap] = useState<Map<string, FloatingAction[]>>(
    new Map(),
  )

  const registerActions = useCallback(
    (actions: FloatingAction[], componentId: string) => {
      setActionMap(prev => {
        const newMap = new Map(prev)
        newMap.set(componentId, actions)
        return newMap
      })
    },
    [],
  )

  const clearActions = useCallback((componentId: string) => {
    setActionMap(prev => {
      const newMap = new Map(prev)
      newMap.delete(componentId)
      return newMap
    })
  }, [])

  // Flatten all actions and sort by priority
  const currentActions = React.useMemo(() => {
    const allActions: FloatingAction[] = []
    actionMap.forEach(actions => {
      allActions.push(...actions)
    })

    // Sort by priority (highest first), then by registration order
    return allActions.sort((a, b) => {
      const priorityA = a.priority ?? 0
      const priorityB = b.priority ?? 0
      return priorityB - priorityA
    })
  }, [actionMap])

  const isVisible = currentActions.length > 0

  return (
    <FloatingActionIslandContext.Provider
      value={{
        registerActions,
        clearActions,
        currentActions,
        isVisible,
      }}
    >
      {children}
    </FloatingActionIslandContext.Provider>
  )
}

export function useFloatingActionIsland() {
  const context = useContext(FloatingActionIslandContext)
  if (!context) {
    throw new Error(
      'useFloatingActionIsland must be used within a FloatingActionIslandProvider',
    )
  }
  return context
}
